// ─────────────────────────────────────────────────────────────
// Cheap, local PHI heuristics for ticket titles.
//
// Runs on every staff view (no network, no AI) and errs toward
// "may contain" — the point is to nudge a human to look, not to
// be a classifier. AI is only involved when the user explicitly
// asks for a rewrite suggestion.
// ─────────────────────────────────────────────────────────────

interface PhiPattern {
  re: RegExp;
  label: string;
}

const PATTERNS: PhiPattern[] = [
  { re: /\b\d{3}-\d{2}-\d{4}\b/, label: 'possible SSN' },
  { re: /\b(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]?\d{4}\b/, label: 'possible phone number' },
  { re: /\b[\w.+-]+@[\w-]+\.[\w.-]+\b/, label: 'email address' },
  { re: /\b(dob|date of birth|born (on|in))\b/i, label: 'date-of-birth reference' },
  { re: /\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/, label: 'date (possible DOB)' },
  {
    re: /\b(mrn|medical record|chart (number|#)|patient (id|#)|member (id|#)|policy (number|#)|insurance (id|number|#))\b/i,
    label: 'medical/insurance identifier',
  },
  {
    re: /\b(patient|rx|prescription|refill|diagnos(is|es|ed)|medicat(ion|ed)|dosage|lab (result|work)s?|bloodwork|icd-?1?0?|surgery|therapy|treatment plan)\b/i,
    label: 'health-related term',
  },
  // A capitalized First Last pair right after a person-ish word. Deliberately
  // narrow (no bare "for X Y") to keep false positives down. Trigger words
  // spell out both cases because the name part must stay case-sensitive.
  {
    re: /\b([Pp]atient|[Mm]ember|[Cc]lient|[Rr]esident|[Mm]rs?|[Mm]s|[Dd]r)\.?\s+[A-Z][a-z]+(\s+[A-Z][a-z]+)+\b/,
    label: 'possible person name',
  },
];

/** Distinct reasons a title looks like it may contain PHI. Empty = clean. */
export function detectPhiInTitle(title: string): string[] {
  if (!title) return [];
  const found: string[] = [];
  for (const p of PATTERNS) {
    if (p.re.test(title) && !found.includes(p.label)) found.push(p.label);
  }
  return found;
}
